import get from "lodash/get";
import set from "lodash/set";
import uniq from "lodash/uniq";
import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useScopedEventName, useEmitter, useListener } from "../../helpers/customEvents";
import { bookNow, CHECKOUT_CONTEXT_ENUM, updateAdminSettings, updateCart, updateCustomerId } from "../../store/actions";
import { BOOK_NOW_EVENT, } from "../BookingForm/booking-form.helpers";
import { CART_EVENT } from "./cart.constants";
import { isAdminIntegration } from "../../store/statePresenter";
import { compareKeys } from "../../helpers/object";
import { useConfig } from "../../AppContext";
export var useCartListener = function (callback, options) {
    if (callback === void 0) { callback = function (cartItems) { }; }
    if (options === void 0) { options = {}; }
    var cb = callback;
    var dispatch = useDispatch();
    var scopedCartEventName = useScopedEventName(CART_EVENT);
    var cartEmitter = useEmitter(scopedCartEventName);
    var cartItems = useSelector(function (state) { return get(state, "cartReducer.cart_items"); });
    var initiator = useSelector(function (state) { return get(state, "cartReducer.initiator"); });
    var storeKey = useSelector(function (state) { return get(state, "store.storeKey"); });
    var cartRef = useRef(JSON.stringify(cartItems));
    useEffect(function () {
        if (get(options, "noEmit", false) || initiator !== storeKey)
            return;
        cartEmitter({ cartItems: cartItems, initiator: initiator });
    }, [JSON.stringify(cartItems), initiator]);
    // Listen to cart events from same tab.
    var onSignalCb = useCallback(function (_a) {
        var _b = _a.detail, cartItems = _b.cartItems, _c = _b.initiator, initiator = _c === void 0 ? storeKey : _c;
        if (cartRef.current === JSON.stringify(cartItems))
            return;
        cartRef.current = JSON.stringify(cartItems);
        dispatch(updateCart(cartItems, cb, initiator));
    }, []);
    useListener(scopedCartEventName, onSignalCb);
};
export var useBookNowListener = function (callback) {
    if (callback === void 0) { callback = function (bookingForm) { }; }
    var cb = callback;
    var dispatch = useDispatch();
    var scopedBookEventName = useScopedEventName(BOOK_NOW_EVENT);
    var bookNowEmitter = useEmitter(scopedBookEventName);
    var globalReducer = useSelector(function (state) { return get(state, "globalReducer"); });
    useEffect(function () {
        if (globalReducer.checkoutContext === CHECKOUT_CONTEXT_ENUM.BOOK_NOW) {
            bookNowEmitter(globalReducer.form);
        }
    }, [JSON.stringify(globalReducer)]);
    var onSignalCb = useCallback(function (_a) {
        var bookingForm = _a.detail;
        dispatch(bookNow(bookingForm, cb));
    }, []);
    useListener(scopedBookEventName, onSignalCb);
};
export var normalizeCart = function (cartForm) {
    if (cartForm === null || cartForm === void 0 ? void 0 : cartForm.cart) {
        set(cartForm, "booking_data.cart_items", getNormalizedCartItems(cartForm.cart));
        delete cartForm.cart;
    }
    if (!(cartForm === null || cartForm === void 0 ? void 0 : cartForm.booking_data)) {
        return;
    }
    return cartForm;
};
export var getNormalizedCartItems = function (items) {
    return items.reduce(function (cartItems, cartItem) {
        if (cartItem.booking_data) {
            return cartItems.concat(cartItem.booking_data.cart_items);
        }
        return cartItems.concat(cartItem);
    }, []);
};
export var getCartItemIds = function (items) {
    var ids = getNormalizedCartItems(items).reduce(function (ids, cartItem) {
        var id = get(cartItem, "booking.course_timespan_id");
        if (!id) {
            return ids;
        }
        return ids.concat(+id);
    }, []);
    return uniq(ids.sort(function (x, y) { return (y - x); }));
};
var defaultAdminSettings = {};
export var compareAdminSettings = function (curr, prev) {
    return compareKeys(curr, prev, function (curr, prev, prop) {
        switch (prop) {
            case "admin_config_toggles": return compareKeys(curr, prev);
            default: return curr === prev;
        }
    });
};
export var ADMIN_SETTINGS_EVENT = "admin-settings-event";
export var useAdminSettingsListener = function () {
    var dispatch = useDispatch();
    var scopedAdminSettingsEventName = useScopedEventName(ADMIN_SETTINGS_EVENT);
    var adminSettingsEmitter = useEmitter(scopedAdminSettingsEventName);
    var adminIntegration = useSelector(function (state) { return isAdminIntegration(state); });
    var adminSettings = useSelector(function (state) { return get(state, "formReducer.forms.adminsettings", defaultAdminSettings); }, compareAdminSettings);
    var adminSettingsRef = useRef({});
    useEffect(function () {
        if (!adminIntegration)
            return;
        if (compareAdminSettings(adminSettingsRef.current, adminSettings))
            return;
        adminSettingsRef.current = adminSettings;
        adminSettingsEmitter(adminSettings);
    }, [JSON.stringify(adminSettings)]);
    var onSignalCb = useCallback(function (_a) {
        var adminSettings = _a.detail;
        if (!adminIntegration)
            return;
        if (compareAdminSettings(adminSettingsRef.current, adminSettings))
            return;
        adminSettingsRef.current = adminSettings;
        dispatch(updateAdminSettings(adminSettings));
    }, []);
    useListener(scopedAdminSettingsEventName, onSignalCb);
};
export var CUSTOMER_ID_EVENT = "customer-id-event";
export var useCustomerIdListener = function () {
    var _a;
    var dispatch = useDispatch();
    var customerIdFromContext = useConfig("customerId")[0];
    var scopedCustomerIdEvent = useScopedEventName(CUSTOMER_ID_EVENT);
    var customerIdEmitter = useEmitter(scopedCustomerIdEvent);
    var adminIntegration = useSelector(function (state) { return isAdminIntegration(state); });
    var customerId = (_a = useSelector(function (state) { return get(state, "appReducer.customerId", customerIdFromContext); })) !== null && _a !== void 0 ? _a : "";
    useEffect(function () {
        adminIntegration && customerIdEmitter(customerId);
    }, [customerId]);
    var onSignalCb = useCallback(function (_a) {
        var customerId = _a.detail;
        adminIntegration && dispatch(updateCustomerId(customerId));
    }, []);
    useListener(scopedCustomerIdEvent, onSignalCb);
};
